'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Search, X, Map, MapPin } from 'lucide-react';
import { MapMode } from './HomepageMapViewInner';
import { useMapControlsContext } from './MapControlsContext';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
}

interface FloatingSearchBarProps {
  mapMode?: MapMode;
  onMapModeChange?: (mode: MapMode) => void;
}

export default function FloatingSearchBar({ mapMode = 'map', onMapModeChange }: FloatingSearchBarProps) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { flyTo } = useMapControlsContext();

  const fetchSuggestions = useCallback(async (value: string) => {
    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }

    if (value.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(value)}`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      if (res.ok) {
        const data: NominatimResult[] = await res.json();
        // Only update if this request wasn't aborted
        if (!controller.signal.aborted) {
          setResults(data);
        }
      }
    } catch (err: unknown) {
      // Ignore abort errors; clear results for other errors
      if (err instanceof Error && err.name !== 'AbortError') {
        setResults([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) {
      // Cancel any pending request and clear immediately
      if (abortRef.current) abortRef.current.abort();
      setResults([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 350);
  };

  const handleSelect = (result: NominatimResult) => {
    setQuery(result.display_name.split(',')[0]);
    setResults([]);
    setFocused(false);
    flyTo(parseFloat(result.lat), parseFloat(result.lon), 15);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setLoading(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  };

  const handleFocus = () => {
    setFocused(true);
    // If query is empty or too short, ensure results are cleared
    if (query.trim().length < 2) {
      setResults([]);
    }
  };

  const showDropdown = focused && (results.length > 0 || (loading && query.length >= 2));
  const isSatellite = mapMode === 'satellite';

  return (
    <div className="search-bar-floating">
      <div className="relative flex items-center gap-2">
        {/* Search pill */}
        <div
          className={`
            flex items-center gap-2 bg-card rounded-full px-4 py-3 shadow-float border flex-1
            transition-all duration-200 min-h-[48px]
            ${focused ? 'border-primary shadow-[0_0_0_3px_rgba(0,86,210,0.12),0_4px_24px_rgba(0,0,0,0.10)]' : 'border-border'}
          `}
        >
          <Search
            size={18}
            className={`flex-shrink-0 transition-colors duration-150 ${focused ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <input
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder="Search locality…"
            className="
              flex-1 bg-transparent border-none outline-none text-sm font-medium
              text-foreground placeholder:text-muted-foreground
              min-w-0
            "
          />
          {query.length > 0 && (
            <button
              onClick={handleClear}
              className="flex-shrink-0 w-5 h-5 rounded-full bg-muted flex items-center justify-center hover:bg-border transition-colors duration-150"
              aria-label="Clear search"
            >
              <X size={11} className="text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Map / Satellite toggle pill */}
        <button
          onClick={() => onMapModeChange?.(isSatellite ? 'map' : 'satellite')}
          aria-label={isSatellite ? 'Switch to map view' : 'Switch to satellite view'}
          className={`
            flex items-center gap-1.5 px-3 py-3 rounded-full shadow-float border min-h-[48px]
            transition-all duration-200 flex-shrink-0 select-none
            ${isSatellite
              ? 'bg-slate-800 border-slate-700 text-white hover:bg-slate-700' :'bg-card border-border text-foreground hover:bg-muted'}
          `}
        >
          {isSatellite ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              <span className="text-xs font-semibold tracking-wide">SAT</span>
            </>
          ) : (
            <>
              <Map size={16} className="flex-shrink-0" />
              <span className="text-xs font-semibold tracking-wide">MAP</span>
            </>
          )}
        </button>

        {/* Suggestions dropdown */}
        {showDropdown && (
          <div className="absolute top-full mt-2 left-0 right-12 bg-card rounded-2xl shadow-float border border-border overflow-hidden z-50">
            {loading && results.length === 0 && (
              <div className="flex items-center gap-3 px-4 py-3 text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                Searching…
              </div>
            )}
            {results.map((result) => {
              const parts = result.display_name.split(',');
              const primary = parts[0]?.trim();
              const secondary = parts.slice(1, 3).join(',').trim();
              return (
                <button
                  key={result.place_id}
                  onMouseDown={() => handleSelect(result)}
                  className="
                    w-full flex items-center gap-3 px-4 py-3 text-left
                    hover:bg-muted transition-colors duration-100
                    border-b border-border last:border-b-0
                  "
                >
                  <MapPin size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{primary}</div>
                    {secondary && (
                      <div className="text-xs text-muted-foreground truncate">{secondary}</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}